import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { getXeroTenants, selectXeroTenant, XeroTenantOption } from "@/api/integration.tsx"
import { toast } from "@/components/ui/use-toast"
import { Building2, Check, Loader2 } from "lucide-react"
import { useEffect, useState } from "preact/hooks"
import { useLocation } from "wouter-preact"

/**
 * After connecting Xero, if the authorizing user granted access to more than one Xero organization we
 * don't silently bind the first — this page makes them choose which organization to use. Reached via
 * redirect from the OAuth callback; on success it returns to the connect flow.
 */
export function XeroSelectOrganizationPage() {
  const [, setLocation] = useLocation()
  const [tenants, setTenants] = useState<XeroTenantOption[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void load()
  }, [])

  async function load() {
    setIsLoading(true)
    setError(null)
    const result = await getXeroTenants()
    if (result.ok) {
      const list = result.value.data.tenants
      setTenants(list)
      setSelectedId(list.find((t) => t.selected)?.tenant_id ?? list[0]?.tenant_id ?? null)
    } else {
      setError("Couldn't load your Xero organizations. Reconnect Xero and try again.")
    }
    setIsLoading(false)
  }

  async function handleConfirm() {
    if (!selectedId) {
      return
    }
    setIsSaving(true)
    const result = await selectXeroTenant(selectedId)
    setIsSaving(false)
    if (result.ok) {
      toast({ title: "Xero organization selected" })
      setLocation("/setup/connect")
    } else {
      setError(result.error.message || "Couldn't save your selection. Please try again.")
    }
  }

  if (isLoading) {
    return (
      <div class="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div class="container mx-auto px-4 py-10 max-w-2xl">
      <Card class="shadow-sm border-slate-200/80">
        <CardHeader class="space-y-1 pb-4">
          <CardTitle class="flex items-center gap-2.5 text-lg font-semibold tracking-tight">
            <Building2 className="size-5 shrink-0 text-muted-foreground" />
            Choose your Xero organization
          </CardTitle>
          <CardDescription>
            Your Xero login has access to more than one organization. Pick the one MarginWiz should sync and post to.
          </CardDescription>
        </CardHeader>
        <CardContent class="space-y-4">
          <div class="space-y-2">
            {tenants.map((tenant) => {
              const is_selected = tenant.tenant_id === selectedId
              return (
                <button
                  key={tenant.tenant_id}
                  type="button"
                  onClick={() => setSelectedId(tenant.tenant_id)}
                  class={`flex w-full items-center justify-between rounded-md border px-4 py-3 text-left transition-colors ${
                    is_selected ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"
                  }`}>
                  <span class="font-medium text-slate-900">{tenant.tenant_name}</span>
                  {is_selected ? <Check className="h-4 w-4 shrink-0 text-blue-600" /> : null}
                </button>
              )
            })}
          </div>

          {error ? (
            <div class="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          ) : null}

          <div class="flex justify-end pt-2">
            <Button
              onClick={() => void handleConfirm()}
              disabled={isSaving || !selectedId}
              class="bg-green-600 hover:bg-green-700 text-white">
              {isSaving ? (
                <span class="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                  Saving…
                </span>
              ) : (
                "Use this organization"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
